import { ApiModule } from '@keenvil/littlefinger';
import ApiConfig from '../apiConfig';

/**
 * Community Module endpoints
 */
class CommunityModule extends ApiModule {
  findCommunityById(id) {
    return this.adapter.find(`c/${id}`);
  }

  updateSettings(settings) {
    if (settings.id) {
      return this.adapter.update(`settings`, settings);
    } else {
      return this.adapter.save(`settings`, settings);
    }
  }

  findInfractionsSettings() {
    return this.adapter.find(`settings`);
  }

  /**
   * Queries the units lists
   * @param {Object} query filter object.
   *                 Contains q, page, limit, and sort.
   *                 All optionals.
   */
  queryUnits(query) {
    return this.adapter.query('units', query);
  }

  findUnitById(id) {
    return this.adapter.find(`units/${id}/info`);
  }

  updateUnitById(unit) {
    return this.adapter.update(`units/${unit.id}`, unit);
  }

  updateResidentAvatarById(id, avatar) {
    return this.adapter.save(`individuals/${id}/avatar`, avatar, {}, false);
  }

  createUnit(unit) {
    return this.adapter.save(`units`, unit);
  }

  deleteUnitById(id) {
    return this.adapter.delete(`units/${id}`, false);
  }

  /**
   * Queries the users list
   * @param {Object} query filter object.
   *                 Contains q, page, limit, and sort.
   *                 All optionals.
   */
  queryUsers(query) {
    return this.adapter.query('individuals/list', query);
  }

  findUserById(id) {
    return this.adapter.find(`individuals/${id}`);
  }

  findUserByIdTypeNumber(idType, number) {
    return this.adapter.query(`individuals`, {
      idType: idType,
      number: number
    });
  }

  updateUserById(user) {
    return this.adapter.update(`individuals/${user.id}`, user);
  }

  assignUnitsToUser(userId, units) {
    return this.adapter.update(`individuals/${userId}/units`, units);
  }

  unassignUnitFromUser(userId, unitId) {
    return this.adapter.delete(`individuals/${userId}/units/${unitId}`, false);
  }

  deleteUserById(id) {
    return this.adapter.delete(`individuals/${id}`, false);
  }

  createUser(user) {
    return this.adapter.save(`individuals/create`, user);
  }

  //Create users in bulk
  uploadUsersFile(fileData) {
    return this.adapter.upload(`individuals/upload`, fileData);
  }

  startBulkUserCreationProcess(jobId) {
    return this.adapter.save(`individuals/batch`, {
      fileUri: jobId
    });
  }

  resendInvitationEmail() {
    return this.adapter.save(`individuals/emails/resend`, {});
  }

  findNationalities() {
    // Son ~243 desde que community-api cargó todas las ISO (15/10/2026); con 150 la
    // lista alfabética se cortaba y faltaban Paraguay, Perú, Uruguay, Venezuela...
    return this.adapter.query('nationalities', { limit: 500 });
  }

  sendWelcomeEmail(id) {
    return this.adapter.save(`individuals/emails/${id}`, {});
  }

  changeIndividualStatus(individualId, profileId, body) {
    return this.adapter.update(
      `individuals/${individualId}/profiles/${profileId}/status`,
      body
    );
  }

  findIndividualCredentials(individualId) {
    return this.adapter.find(`individuals/${individualId}/credentials`);
  }

  findPermissionsCatalog() {
    return this.adapter.find('permissions');
  }

  findIndividualProfilePermissions(individualId, profileId) {
    return this.adapter.find(
      `individuals/${individualId}/profiles/${profileId}/permissions`
    );
  }

  // Grant (PUT) y revoke (DELETE) leen `reason` como @RequestParam, no del
  // body JSON. Mandarlo en el body tira 422 "reason is required" aunque el
  // front sí lo esté enviando — misma convención que revokeContract.
  grantIndividualProfilePermission(individualId, profileId, key, reason) {
    const path = `individuals/${individualId}/profiles/${profileId}/permissions/${key}?reason=${encodeURIComponent(
      reason || ''
    )}`;

    return this.adapter.update(path, {});
  }

  revokeIndividualProfilePermission(individualId, profileId, key, reason) {
    const path = `individuals/${individualId}/profiles/${profileId}/permissions/${key}?reason=${encodeURIComponent(
      reason || ''
    )}`;

    return this.adapter.delete(path);
  }

  updateIndividualCardCredential(individualId, cardNumber) {
    return this.adapter.update(`individuals/${individualId}/credentials/card`, {
      cardNumber
    });
  }

  blockIndividualCardCredential(individualId) {
    return this.adapter.update(
      `individuals/${individualId}/credentials/card/block`
    );
  }

  deleteIndividualCardCredential(individualId) {
    return this.adapter.delete(
      `individuals/${individualId}/credentials/card`,
      false
    );
  }

  updateIndividualCodeCredential(individualId, accessCode) {
    return this.adapter.update(`individuals/${individualId}/credentials/code`, {
      accessCode
    });
  }

  deleteIndividualCodeCredential(individualId) {
    return this.adapter.delete(
      `individuals/${individualId}/credentials/code`,
      false
    );
  }

  findIndividualZoneAccessSyncStatus(communityId, individualId, provider) {
    const currentCommunityId = this.adapter.communityId;
    this.adapter.communityId = null;
    const providerParam = provider ? `?provider=${provider}` : '';

    try {
      return this.adapter.find(
        `zoneaccess/sync/communities/${communityId}/individuals/${individualId}/status${providerParam}`
      );
    } finally {
      this.adapter.communityId = currentCommunityId;
    }
  }

  syncIndividualZoneAccess(communityId, individualId) {
    return this.withUnscopedCommunityRequest(() =>
      this.adapter.save(
        `zoneaccess/sync/communities/${communityId}/individuals/${individualId}`,
        {}
      )
    );
  }

  findAccessLevels() {
    return this.adapter.find('access-levels');
  }

  findZkAccessLevels(communityId) {
    return this.withUnscopedCommunityRequest(() =>
      this.adapter.find(`zoneaccess/zk/levels?communityId=${communityId}`)
    );
  }

  /**
   * Devices and doors of a community with their live online state (agnostic ZK/DSS).
   * The community goes explicitly in the path so a community switch never reads another one's devices.
   */
  findCommunityDevices(communityId) {
    return this.withUnscopedCommunityRequest(() =>
      this.adapter.find(`c/${communityId}/devices`)
    );
  }

  /**
   * Morosidad: episodios activos de la comunidad (unitId, unitNumber, suspendedAt, reason).
   * Sin paginar; vacío si la comunidad tiene la morosidad apagada.
   */
  findActiveAccessSuspensions(communityId) {
    return this.withUnscopedCommunityRequest(() =>
      this.adapter.find(`c/${communityId}/access-suspensions?active=true`)
    );
  }

  withUnscopedCommunityRequest(request) {
    const currentCommunityId = this.adapter.communityId;
    this.adapter.communityId = null;

    try {
      return request();
    } finally {
      this.adapter.communityId = currentCommunityId;
    }
  }

  findZoneAccessAssignments(communityId) {
    return this.withUnscopedCommunityRequest(() =>
      this.adapter.find(`zoneaccess/assignments?communityId=${communityId}`)
    );
  }

  /**
   * Same listing as findZoneAccessAssignments but filtered by subject, so
   * pages that only need one individual's (or one unit's) rows don't have
   * to pull the whole community's assignment table. See fix #2 in
   * castleblack-fixes-zonas-dss.md.
   */
  findZoneAccessAssignmentsForSubject(
    communityId,
    subjectType,
    subjectId,
    includeInherited = false
  ) {
    const inheritedParam = includeInherited ? '&includeInherited=true' : '';

    return this.withUnscopedCommunityRequest(() =>
      this.adapter.find(
        `zoneaccess/assignments?communityId=${communityId}&subjectType=${subjectType}&subjectId=${subjectId}${inheritedParam}`
      )
    );
  }

  /**
   * Sync summary of a unit's residents for one provider (ZK). Shape:
   * { total, synced, pending, failed, timedOut, notSynced, allSynced, fallos }.
   */
  findUnitZoneAccessSyncSummary(communityId, unitId, provider) {
    return this.withUnscopedCommunityRequest(() =>
      this.adapter.find(
        `zoneaccess/sync/communities/${communityId}/units/${unitId}/summary?provider=${provider}`
      )
    );
  }

  createZoneAccessAssignment(assignment) {
    return this.withUnscopedCommunityRequest(() =>
      this.adapter.save('zoneaccess/assignments', assignment)
    );
  }

  deleteZoneAccessAssignment(assignmentId) {
    return this.withUnscopedCommunityRequest(() =>
      this.adapter.delete(`zoneaccess/assignments/${assignmentId}`, false)
    );
  }

  findCommunityGates() {
    return this.adapter.find(`gates`);
  }

  /**
   * Morosidad (access suspensions) — a single admin action that cuts both
   * face (DSS) and zones (ZK) for a subject. Unlike the zones endpoints
   * above, this controller's path already includes `/c/{communityId}` —
   * which is exactly what the adapter's default scoped request already
   * produces (same pattern as `findAccessLevels`), so these don't need
   * `withUnscopedCommunityRequest`.
   * `subjectType` is `'GROUP'` (subjectId = individual_group_id, cuts the
   * whole group) or `'INDIVIDUAL'` (subjectId = individual_id).
   */
  applyAccessSuspension({ subjectType, subjectId, unitId, reason }) {
    return this.adapter.save('access-suspensions', {
      subjectType,
      subjectId,
      unitId,
      reason
    });
  }

  // The back answers 204 No Content on success (nothing to parse) and a JSON error list
  // on failure. adapter.save can only read both as JSON (an empty 204 breaks) or both as
  // text (a JSON error ends as a JS error and the real reason is lost), so the response
  // is handled here: success without parsing, errors as JSON (apiErrors with message/status).
  liftAccessSuspension({ subjectType, subjectId }) {
    const adapter = this.adapter;
    return adapter.transport
      .post(
        adapter.getHeaders(true),
        adapter.buildUrl('access-suspensions/lift'),
        { subjectType, subjectId }
      )
      .then(response =>
        response.ok ? null : adapter.processResponse(response, true)
      );
  }

  // Episodes for every subject suspended on this unit (GROUP and
  // INDIVIDUAL); an episode with liftedAt === null is active.
  findUnitAccessSuspensions(unitId) {
    return this.adapter.find(`access-suspensions/unit/${unitId}`);
  }

  // Optional per-user morosidad view (not wired in v1) — `{ suspended,
  // appliedCapabilities }`.
  findIndividualAccessSuspensionStatus(individualId) {
    return this.adapter.find(`access-suspensions/individual/${individualId}`);
  }

  /**
   * Queries the users list
   * @param {Object} query filter object.
   *                 Contains q, page, limit, and sort.
   *                 All optionals.
   */
  search(query) {
    return this.adapter.query('individuals/list', query);
  }

  assignVehicle(individualId, unitId, vehicle) {
    const path = `individuals/${individualId}/units/${unitId}/vehicles`;
    return this.adapter.save(path, vehicle);
  }

  editVehicle(individualId, unitId, vehicleId, newData) {
    const path = `individuals/${individualId}/units/${unitId}/vehicles/${vehicleId}`;
    return this.adapter.update(path, newData);
  }

  unassignVehicle(individualId, unitId, vehicle) {
    const path = `individuals/${individualId}/units/${unitId}/vehicles/${vehicle}`;
    return this.adapter.delete(path, false);
  }

  getUserData(individualId) {
    return this.adapter.find(`individuals/${individualId}`);
  }

  getVehicleUnit(groupId) {
    const path = `groups/${groupId}`;
    return this.adapter.find(path);
  }

  getSettings() {
    return this.adapter.find('settings');
  }

  getCreatedContracts(query) {
    return this.adapter.query('contracts', query);
  }

  createContract(contractObject) {
    const path = `contracts`;
    return this.adapter.save(path, contractObject);
  }

  updateContract(profileType, individualId, contractId) {
    return this.adapter.update(
      `individuals/${individualId}/contract?profileType=${profileType}&contractId=${contractId}`
    );
  }

  validateContract(contractId, validateObject) {
    const path = `contracts/${contractId}/status`;
    return this.adapter.update(path, {
      ...validateObject,
      ownersId: [],
      tenantsId: []
    });
  }

  uploadContractFileToContract(contractId, file) {
    const path = `contracts/${contractId}`;
    return this.adapter.update(path, file);
  }

  revokeContract(contractId, reason) {
    const path = `contracts/${contractId}?reason=${reason}`;
    return this.adapter.delete(path, false);
  }

  getContractOwnersById(ownerSetValues) {
    const query = {
      individualsId: ownerSetValues
    };
    return this.adapter.query('individuals/list', query);
  }

  addCoresidents(contractId, userId) {
    return this.adapter.update(`contracts/${contractId}`, userId[0]);
  }

  updateContractDates(contractId, contract) {
    return this.adapter.update(`contracts/${contractId}`, {
      ...contract,
      ownersId: [],
      tenantsId: []
    });
  }

  uploadContract(file) {
    return this.adapter.upload(`contracts/document`, file);
  }

  sendNotification(unitId, id) {
    return this.adapter.save('notifications?type=CORRESPONDENCE', {
      unitId: unitId,
      recipientId: id
    });
  }

  addNewPetToUser(petObject) {
    const pet = petObject.pet;
    return this.adapter.save(
      `individuals/${petObject.userId}/units/${petObject.unitId}/pets`,
      pet
    );
  }

  updatePet(petObject) {
    const pet = petObject.pet;
    return this.adapter.update(
      `individuals/${petObject.userId}/units/${petObject.unitId}/pets/${petObject.petId}`,
      pet
    );
  }

  getIndividual(individualId) {
    return this.adapter.find(`groups/${individualId}`);
  }

  getIndividuals({ idType, number, idCountry }) {
    const country = idCountry
      ? `&idCountry=${encodeURIComponent(idCountry)}`
      : '';
    return this.adapter.find(
      `individuals?idType=${encodeURIComponent(
        idType
      )}&number=${encodeURIComponent(number)}${country}`
    );
  }

  // Catálogo de documentos por país + país por defecto de la comunidad.
  findDocumentTypes(communityId) {
    return this.withUnscopedCommunityRequest(() =>
      this.adapter.find(`c/${communityId}/document-types`)
    );
  }

  deletePet(petId, userId, unitId) {
    return this.adapter.delete(
      `individuals/${userId}/units/${unitId}/pets/${petId}`,
      false
    );
  }

  getContracts(query) {
    const keys = Object.keys(query);
    let Query = '?';
    let i;
    for (i = 0; i < keys.length; i++) {
      Query =
        Query +
        `${keys[i]}=${query[keys[i]]}` +
        (i == keys.length - 1 ? '' : '&');
    }

    return this.adapter.find(`contracts/dates` + Query);
  }

  findIndividualUnits(individualId) {
    return this.adapter.find(`individuals/${individualId}/units`);
  }

  queryMultipleUnits(unitsIdList, limit) {
    //can be Array or string for example "234,123,454"
    const unitsIds =
      unitsIdList.length > 1 ? unitsIdList.join(',') : unitsIdList;
    return this.adapter.find(
      `units/complete?unitIds=${unitsIds}&Limit=${limit}`
    );
  }

  findByEmail(emailAddress) {
    return this.adapter.query(`individuals/email`, { emailAddress });
  }
}

export default new CommunityModule('community', ApiConfig);
